<template>
  <div class="flex h-full overflow-hidden">
    <!-- Resource-type navigation, built entirely from `registry` -->
    <div class="flex flex-col w-28 flex-shrink-0 border-r border-slate-600 overflow-y-auto bg-slate-800/40">
      <button
        v-for="entry in registry"
        :key="entry.type"
        class="text-left px-3 py-2 text-xs font-medium transition-colors flex items-center gap-1.5"
        :class="entry.type === currentType
          ? 'bg-slate-500 text-white'
          : 'text-slate-400 hover:bg-slate-700 hover:text-white'"
        :title="entry.displayName"
        @click="select(entry.type)"
      >
        <font-awesome-icon v-if="entry.icon" :icon="entry.icon" class="flex-shrink-0" />
        <span class="truncate">{{ entry.displayName }}</span>
      </button>
    </div>

    <!-- Selected resource type's own manager component, or a placeholder -->
    <div class="flex-1 min-w-0 overflow-hidden flex flex-col">
      <component
        v-if="activeEntry?.managerComponent"
        :is="activeEntry.managerComponent"
        v-bind="activeEntry.getProps ? activeEntry.getProps(context) : {}"
        v-on="activeEntry.getListeners ? activeEntry.getListeners(context) : {}"
      />
      <div v-else class="flex-1 flex items-center justify-center text-xs text-slate-500 p-6 text-center">
        {{ activeEntry?.displayName ?? 'This resource type' }} isn't available here yet.
      </div>
    </div>
  </div>
</template>

<script setup>
/**
 * ResourceManagerShell.vue — Phase 5 of the Project Resource architecture
 * (see docs/PROJECT-RESOURCE-ARCHITECTURE-PLAN.md).
 *
 * Generic host for the Designer's unified Resources section: navigation,
 * layout, and an empty-state placeholder for resource types with no manager
 * yet. It knows nothing about datasets, fonts, or any concrete resource
 * type — everything it renders comes from the `registry` prop (see
 * resourceUIRegistry.js) and the `context` prop it forwards through
 * each entry's own getProps()/getListeners() mapping.
 */
import { computed } from 'vue'

const props = defineProps({
  registry: { type: Array, required: true },
  context: { type: Object, default: () => ({}) },
  activeType: { type: String, default: null },
})
const emit = defineEmits(['update:activeType'])

const currentType = computed(() => props.activeType ?? props.registry[0]?.type ?? null)
const activeEntry = computed(() => props.registry.find(e => e.type === currentType.value) ?? null)

function select(type) {
  emit('update:activeType', type)
}
</script>
